/**
 * Campo de senha com opção de revelar.
 *
 * Esconder a senha protege contra quem olha por cima do ombro — um risco real, mas
 * ocasional. Digitar às cegas produz erro em toda tentativa, e o remédio comum das
 * pessoas é pior que a doença: escolher senhas curtas e fáceis de digitar, ou colar
 * do gerenciador para um bloco de notas para conferir. Deixar revelar sob demanda
 * custa pouco e sustenta senhas longas, que é o que o Argon2 precisa para valer.
 *
 * O estado começa oculto sempre. Revelar é ação, não configuração persistida.
 */
import { Eye, EyeOff } from 'lucide-react';
import { useId, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export function CampoSenha({
  rotulo,
  valor,
  onChange,
  autoComplete = 'current-password',
  minLength,
  ajuda,
  erro,
  autoFocus,
  id,
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  autoComplete?: 'current-password' | 'new-password';
  minLength?: number;
  ajuda?: string;
  erro?: string;
  autoFocus?: boolean;
  id?: string;
}) {
  const gerado = useId();
  const campoId = id ?? gerado;
  const [revelada, setRevelada] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={campoId}>{rotulo}</Label>
      <div className="relative">
        <Input
          id={campoId}
          type={revelada ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          minLength={minLength}
          autoFocus={autoFocus}
          aria-invalid={Boolean(erro)}
          aria-describedby={erro || ajuda ? `${campoId}-ajuda` : undefined}
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          // Espaço à direita para o botão não cobrir o texto digitado.
          className={cn('pr-11', revelada && 'font-mono')}
        />
        <button
          type="button"
          onClick={() => setRevelada((v) => !v)}
          aria-label={revelada ? 'Ocultar senha' : 'Mostrar senha'}
          aria-pressed={revelada}
          // `tabIndex={-1}`: quem navega por teclado espera que Tab vá do campo
          // para o botão de enviar, não para um alternador visual.
          tabIndex={-1}
          className="absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-md text-text-tertiary outline-none transition-colors duration-[var(--dur-fast)] hover:text-accent focus-visible:shadow-[var(--focus-ring)]"
        >
          {revelada ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {(erro || ajuda) && (
        <p
          id={`${campoId}-ajuda`}
          className={cn('tipo-corpo-pequeno', erro ? 'text-danger' : 'text-text-tertiary')}
        >
          {erro ?? ajuda}
        </p>
      )}
    </div>
  );
}
